import { action, observable, makeObservable } from 'mobx';
import { Model, IModelAttributes, attribute, entity } from 'Models/Model';
import * as Models from 'Models/Entities';

export interface IDefaultPricingEntityAttributes extends IModelAttributes {
	name: string;
	isDefault: boolean;

	addOnsPrices: Array<Models.AddOnsPriceEntity | Models.IAddOnsPriceEntityAttributes>;
	prices: Array<Models.PriceEntity | Models.IPriceEntityAttributes>;
	userDisplayName?: Models.UserDisplayNameEntity | Models.IUserDisplayNameEntityAttributes;
	pricingStrategyProfileId?: string;
	pricingStrategyProfile?: Models.PricingStrategyProfileEntity | Models.IPricingStrategyProfileEntityAttributes;
}
@entity('DefaultPricingEntity', 'Default Pricing')
export default class DefaultPricingEntity extends Model implements IDefaultPricingEntityAttributes {
	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	@observable
	@attribute<DefaultPricingEntity, string>()
	public name: string;

	@observable
	@attribute<DefaultPricingEntity, boolean>()
	public isDefault: boolean;

	@observable
	@attribute({ isReference: true, manyReference: true })
	public addOnsPrices: Models.AddOnsPriceEntity[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	public prices: Models.PriceEntity[] = [];

	@observable
	@attribute({ isReference: true, manyReference: false })
	public userDisplayName?: Models.UserDisplayNameEntity;

	@observable
	@attribute()
	public pricingStrategyProfileId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public pricingStrategyProfile: Models.PricingStrategyProfileEntity;

	constructor(attributes?: Partial<IDefaultPricingEntityAttributes>) {
		super(attributes);

		this.addOnsPrices = [];
		this.prices = [];

		makeObservable(this);

		this.assignAttributes(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	public assignAttributes(attributes?: Partial<IDefaultPricingEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.name !== undefined) {
				this.name = attributes.name;
			}
			if (attributes.isDefault !== undefined) {
				this.isDefault = attributes.isDefault;
			}
			if (attributes.addOnsPrices !== undefined && Array.isArray(attributes.addOnsPrices)) {
				if (!this.addOnsPrices) {
					this.addOnsPrices = [];
				}
				for (const model of attributes.addOnsPrices) {
					if (model instanceof Models.AddOnsPriceEntity) {
						this.addOnsPrices.push(model);
					} else {
						this.addOnsPrices.push(new Models.AddOnsPriceEntity(model));
					}
				}
			}
			if (attributes.prices !== undefined && Array.isArray(attributes.prices)) {
				if (!this.prices) {
					this.prices = [];
				}
				for (const model of attributes.prices) {
					if (model instanceof Models.PriceEntity) {
						this.prices.push(model);
					} else {
						this.prices.push(new Models.PriceEntity(model));
					}
				}
			}
			if (attributes.userDisplayName !== undefined) {
				if (attributes.userDisplayName === null) {
					this.userDisplayName = attributes.userDisplayName;
				} else if (attributes.userDisplayName instanceof Models.UserDisplayNameEntity) {
					this.userDisplayName = attributes.userDisplayName;
				} else {
					this.userDisplayName = new Models.UserDisplayNameEntity(attributes.userDisplayName);
				}
			}
			if (attributes.pricingStrategyProfileId !== undefined) {
				this.pricingStrategyProfileId = attributes.pricingStrategyProfileId;
			}
			if (attributes.pricingStrategyProfile !== undefined) {
				if (attributes.pricingStrategyProfile === null) {
					this.pricingStrategyProfile = attributes.pricingStrategyProfile;
				} else if (attributes.pricingStrategyProfile instanceof Models.PricingStrategyProfileEntity) {
					this.pricingStrategyProfile = attributes.pricingStrategyProfile;
					this.pricingStrategyProfileId = attributes.pricingStrategyProfile.id;
				} else {
					this.pricingStrategyProfile = new Models.PricingStrategyProfileEntity(
						attributes.pricingStrategyProfile,
					);
					this.pricingStrategyProfileId = this.pricingStrategyProfile.id;
				}
			}
		}
	}
}
